import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Spin, message, Modal, Divider, Badge, Space, Table } from "antd";
import { CheckCircleOutlined, FileOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import "./MeetingApprovalScreen.css";

const MeetingApprovalScreen = ({ meetingData, onApprove, loading = false }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [approving, setApproving] = useState(false);

  if (!meetingData) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" tip="Loading meeting details..." />
      </div>
    );
  }

  const {
    meeting_id,
    district,
    block,
    gram_panchayat,
    sabha_date,
    start_time,
    venue,
    presiding_officer_name,
    presiding_officer_designation,
    presiding_officer_mobile,
    email_id,
    type_of_gram_sabha,
    gp_voter_count,
    pdf_url,
    status,
    created_at,
    agendaItems = [],
  } = meetingData;

  const handleApprove = async () => {
    Modal.confirm({
      title: "Approve Meeting Notice",
      content: "Are you sure you want to approve this meeting notice? It will be moved to the signed folder.",
      okText: "Approve",
      cancelText: "Cancel",
      okButtonProps: { danger: false },
      onOk: async () => {
        setApproving(true);
        try {
          await onApprove(meetingData);
          message.success("Meeting notice approved successfully!");
        } catch (error) {
          message.error("Error approving meeting notice");
          console.error(error);
        } finally {
          setApproving(false);
        }
      },
    });
  };

  const handleViewPDF = () => {
    if (pdf_url) {
      window.open(pdf_url, "_blank");
    } else {
      message.error("PDF URL not available");
    }
  };

  const handleDownloadPDF = () => {
    if (pdf_url) {
      const link = document.createElement("a");
      link.href = pdf_url;
      link.download = `Meeting_Notice_${gram_panchayat}_${sabha_date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success("PDF download started");
    } else {
      message.error("PDF URL not available");
    }
  };

  const agendaColumns = [
    {
      title: "S.No",
      dataIndex: "sequentialNumber",
      key: "sequentialNumber",
      width: 80,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "विषय (Topic)",
      dataIndex: "hindi",
      key: "hindi",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "English",
      dataIndex: "english",
      key: "english",
      render: (text) => <span style={{ fontSize: "12px", color: "#666" }}>{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (category) => (
        <Badge
          color={category === "mandatory" ? "#52C41A" : "#4763E4"}
          text={category === "mandatory" ? "अनिवार्य" : "वैकल्पिक"}
        />
      ),
    },
  ];

  return (
    <div className="meeting-approval-container">
      <div className="approval-header">
        <h1>📋 Meeting Notice Approval</h1>
        <p>Review and approve the meeting notice before finalizing</p>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column - Details */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CheckCircleOutlined style={{ color: "#4763E4" }} />
                <span>Meeting Details</span>
              </div>
            }
            className="detail-card"
          >
            <div className="detail-section">
              <h3>📍 Location Information</h3>
              <div className="detail-row">
                <span className="label">District:</span>
                <span className="value">{district}</span>
              </div>
              <div className="detail-row">
                <span className="label">Block:</span>
                <span className="value">{block}</span>
              </div>
              <div className="detail-row">
                <span className="label">Gram Panchayat:</span>
                <span className="value">{gram_panchayat}</span>
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h3>📅 Meeting Schedule</h3>
              <div className="detail-row">
                <span className="label">Date:</span>
                <span className="value">{sabha_date}</span>
              </div>
              <div className="detail-row">
                <span className="label">Time:</span>
                <span className="value">{start_time}</span>
              </div>
              <div className="detail-row">
                <span className="label">Venue:</span>
                <span className="value">{venue}</span>
              </div>
              <div className="detail-row">
                <span className="label">Type:</span>
                <span className="value">{type_of_gram_sabha}</span>
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h3>👤 Presiding Officer</h3>
              <div className="detail-row">
                <span className="label">Name:</span>
                <span className="value">{presiding_officer_name}</span>
              </div>
              <div className="detail-row">
                <span className="label">Designation:</span>
                <span className="value">{presiding_officer_designation}</span>
              </div>
              <div className="detail-row">
                <span className="label">Mobile:</span>
                <span className="value">{presiding_officer_mobile}</span>
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h3>📧 Contact Information</h3>
              <div className="detail-row">
                <span className="label">Email:</span>
                <span className="value">{email_id}</span>
              </div>
              <div className="detail-row">
                <span className="label">Voters:</span>
                <span className="value">{gp_voter_count}</span>
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h3>📊 Status</h3>
              <div className="detail-row">
                <span className="label">Status:</span>
                <Badge
                  color={status === "approved" ? "#52C41A" : "#FAAD14"}
                  text={status === "approved" ? "Approved" : "Pending"}
                />
              </div>
              <div className="detail-row">
                <span className="label">Created:</span>
                <span className="value">{new Date(created_at).toLocaleString()}</span>
              </div>
            </div>
          </Card>
        </Col>

        {/* Right Column - PDF Preview & Actions */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FileOutlined style={{ color: "#4763E4" }} />
                <span>Meeting Notice PDF</span>
              </div>
            }
            className="pdf-card"
          >
            <div className="pdf-preview-section">
              {pdf_url ? (
                <div className="pdf-viewer">
                  <iframe
                    src={pdf_url}
                    style={{
                      width: "100%",
                      height: "500px",
                      border: "1px solid #E8E8E8",
                      borderRadius: "8px",
                    }}
                    title="Meeting Notice PDF"
                  />
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  <FileOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
                  <p>PDF not available</p>
                </div>
              )}
            </div>

            <Divider />

            <div className="action-buttons">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={handleViewPDF}
                  block
                  size="large"
                  style={{ backgroundColor: "#4763E4" }}
                >
                  View Full PDF
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPDF}
                  block
                  size="large"
                >
                  Download PDF
                </Button>
                <Button
                  type="primary"
                  danger={false}
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  block
                  size="large"
                  loading={approving}
                  disabled={status === "approved"}
                  style={{
                    backgroundColor: status === "approved" ? "#999" : "#52C41A",
                    borderColor: status === "approved" ? "#999" : "#52C41A",
                  }}
                >
                  {status === "approved" ? "✓ Already Approved" : "Approve & Sign"}
                </Button>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Agenda Items Table */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>📝 Agenda Items</span>
            <Badge count={agendaItems.length} style={{ backgroundColor: "#4763E4" }} />
          </div>
        }
        style={{ marginTop: "24px" }}
      >
        <Table
          columns={agendaColumns}
          dataSource={agendaItems}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default MeetingApprovalScreen;
